import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { ClientTheme, ClientThemeFormProps } from './ClientTheme.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../../hooks/useAxiosPrivate'
import Common from '../../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import PageHeader from '../../../../layout/page/PageHeader'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../../common/forms/fields/TextField'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import { handleAxiosErrors } from '../../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../../common/forms/errors/RenderFieldsErrors'
import FormActions from '../../../../common/forms/FormActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faHandPointRight,
} from '@fortawesome/free-solid-svg-icons'
import SelectField from '../../../../common/forms/fields/SelectField'
import ColourField from '../../../../common/forms/fields/ColourField'
import ImageField from '../../../../common/forms/fields/ImageField'
import RenderFormikErrors from '../../../../common/forms/errors/RenderFormikErrors'

const ClientThemeForm: FC<ClientThemeFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'CLIENT_THEME_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptyClientTheme: ClientTheme = {
        name: '',
        code: '',
        client_identity_id: null,
        primary_colour: null,
        secondary_colour: null,
        logo_coloured_light_id: null,
        logo_coloured_dark_id: null,
        logo_contrast_light_id: null,
        logo_contrast_dark_id: null,
        active: false,
        default: false,
    }

    const ClientThemeFormSchema = Yup.object().shape({
        name: Yup.string().trim().required('Enter theme name'),
        code: Yup.string().trim().required('Enter a unique theme code'),
        client_identity_id: Yup.string().trim().required('Select an identity'),
        primary_colour: Yup.string()
            .trim()
            .required('Enter theme primary colour'),
        secondary_colour: Yup.string()
            .trim()
            .required('Enter theme secondary colour'),
    })

    const [clientThemeDialog, setClientThemeDialog] = useState(true)
    const [clientTheme, setClientTheme] =
        useState<ClientTheme>(emptyClientTheme)
    const [availableClientIdentities, setAvailableClientIdentities] = useState<
        []
    >([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const [
        allowRemoveLightColouredLogoImage,
        setAllowRemoveLightColouredLogoImage,
    ] = useState(false)
    const [lightColouredLogoPreviewSrc, setLightColouredLogoPreviewSrc] =
        useState('')

    const [
        allowRemoveDarkColouredLogoImage,
        setAllowRemoveDarkColouredLogoImage,
    ] = useState(false)
    const [darkColouredLogoPreviewSrc, setDarkColouredLogoPreviewSrc] =
        useState('')

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/administration/client/theme/' + id
            : '/administration/client/theme/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                setAvailableClientIdentities(
                    response.data.data.available_client_identities
                )
                if (response.data.data?.theme) {
                    setClientTheme(response.data.data.theme)

                    if (isViewOrEdit) {
                        setAllowRemoveLightColouredLogoImage(
                            response.data.data.theme.logo_coloured_light_id !==
                                null
                        )
                        setLightColouredLogoPreviewSrc(
                            response.data.data.theme.logo_coloured_light_id
                                ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.theme.logo_coloured_light_id}`
                                : ''
                        )

                        setAllowRemoveDarkColouredLogoImage(
                            response.data.data.theme.logo_coloured_dark_id !==
                                null
                        )
                        setDarkColouredLogoPreviewSrc(
                            response.data.data.theme.logo_coloured_dark_id
                                ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.theme.logo_coloured_dark_id}`
                                : ''
                        )
                    } else {
                        setLightColouredLogoPreviewSrc('')
                        setDarkColouredLogoPreviewSrc('')
                    }
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                console.log('err: ', err)
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load client Theme')
            }
        }
    }, [axiosPrivate, id, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            const routePath = isViewOrEdit
                ? '/administration/client/theme/' + id
                : '/administration/client/theme/create'
            const response = await axiosPrivate
                .post(routePath, record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setClientThemeDialog(false)
        navigate('/administration/client/theme')
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/administration/client/theme/delete/' + id}
                    record={clientTheme}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    message={
                        <p className="mb-3 pb-3 border-bottom">
                            Are you sure you want to delete this Theme?
                        </p>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'clientTheme-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...clientTheme,
                    business_name: pageCode + '_BUSINESS_NAME',
                    business_slogan: pageCode + '_BUSINESS_SLOGAN',
                    business_short_description:
                        pageCode + '_BUSINESS_SHORT_DESCRIPTION',
                }}
                validationSchema={ClientThemeFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="clientTheme-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <SelectField
                                                    id="formClientThemeClientIdentity"
                                                    name="client_identity_id"
                                                    label="Brand identity"
                                                    formikProps={formikProps}
                                                    choices={
                                                        availableClientIdentities
                                                    }
                                                    required
                                                />
                                                <TextField
                                                    id="formClientThemeName"
                                                    name="code"
                                                    label="Theme Code"
                                                    placeholder="Enter Theme Code"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                                <TextField
                                                    id="formClientThemeName"
                                                    name="name"
                                                    label="Theme Name"
                                                    placeholder="Enter Theme Name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                                <Row>
                                                    <Col xl={6}>
                                                        <ColourField
                                                            id="formClientThemePrimaryColour"
                                                            name="primary_colour"
                                                            label="Primary Colour"
                                                            placeholder="Enter Primary Colour"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                    <Col xl={6}>
                                                        <ColourField
                                                            id="formClientThemeSecondaryColour"
                                                            name="secondary_colour"
                                                            label="Secondary Colour"
                                                            placeholder="Enter Secondary Colour"
                                                            formikProps={
                                                                formikProps
                                                            }
                                                            required
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col xl={3}>
                                                <ImageField
                                                    id="logoColouredLightImageFile"
                                                    name="logo_coloured_light_id"
                                                    label="Logo Light"
                                                    imgBgClass="bg-light p-5"
                                                    formikProps={formikProps}
                                                    imageId={
                                                        clientTheme.logo_coloured_light_id
                                                    }
                                                    alt={`${clientTheme.name} light image`}
                                                    allowRemoveImage={
                                                        allowRemoveLightColouredLogoImage
                                                    }
                                                    setAllowRemoveImage={
                                                        setAllowRemoveLightColouredLogoImage
                                                    }
                                                    previewSrc={
                                                        lightColouredLogoPreviewSrc
                                                    }
                                                    setPreviewSrc={
                                                        setLightColouredLogoPreviewSrc
                                                    }
                                                    updateRecord={
                                                        setClientTheme
                                                    }
                                                    required
                                                />
                                            </Col>
                                            <Col xl={3}>
                                                <ImageField
                                                    id="logoColouredDarkImageFile"
                                                    name="logo_coloured_dark_id"
                                                    label="Logo Dark"
                                                    imgBgClass="bg-dark p-5"
                                                    formikProps={formikProps}
                                                    imageId={
                                                        clientTheme.logo_coloured_dark_id
                                                    }
                                                    alt={`${clientTheme.name} dark image`}
                                                    allowRemoveImage={
                                                        allowRemoveDarkColouredLogoImage
                                                    }
                                                    setAllowRemoveImage={
                                                        setAllowRemoveDarkColouredLogoImage
                                                    }
                                                    previewSrc={
                                                        darkColouredLogoPreviewSrc
                                                    }
                                                    setPreviewSrc={
                                                        setDarkColouredLogoPreviewSrc
                                                    }
                                                    updateRecord={
                                                        setClientTheme
                                                    }
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Status</legend>
                                        <Row>
                                            <Col xl={6}>
                                                <Form.Group
                                                    controlId={
                                                        'formClientThemeActiveFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Active"
                                                        name="active"
                                                        checked={
                                                            formikProps.values
                                                                .active
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'active',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={6}>
                                                <Form.Group
                                                    controlId={
                                                        'formClientThemeDefaultFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Default"
                                                        name="default"
                                                        checked={
                                                            formikProps.values
                                                                .default
                                                        }
                                                        onChange={(e) =>
                                                            formikProps.setFieldValue(
                                                                'default',
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xl={12}>
                                                <Alert
                                                    variant={
                                                        formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info'
                                                    }
                                                    className={
                                                        (formikProps.values
                                                            .default
                                                            ? 'warning'
                                                            : 'info') +
                                                        ' mt-3 mb-1'
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faHandPointRight}
                                                        className="me-2"
                                                    />{' '}
                                                    When a theme is set to{' '}
                                                    <strong>default</strong> the
                                                    system will automatically
                                                    mark it as{' '}
                                                    <strong>Active</strong>
                                                </Alert>
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="clientTheme-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={clientThemeDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '50vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit ' + clientTheme.name
                                : 'Create new Theme'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : 'Edit ' + clientTheme.name
                                : 'Create new Theme'
                        }
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default ClientThemeForm
